import {useActorRef, useSelector} from '@xstate/react';
import React, {createContext, useContext, PropsWithChildren, useEffect} from 'react';
import {ActorRefFrom, ActorRefFromLogic} from 'xstate';

import {AnnotateFillPlateModalInput, AnnotateFillPlateModalMachine} from './AnnotateFillPlateMachine';

/**
 * @xstate/react provides a createActorContext but we are creating our own version of that here.
 * Reasoning is that we receive new props from the parent that need to be sent to the machine,
 * therefore we need access to the actorRef in the provider component to respond to prop/input changes.
 */
// This eslint rule is unneccessary and should be removed.
// eslint-disable-next-line benchlint/no-react-context
const AnnotateFillPlateMachineCtx = createContext<ActorRefFromLogic<
  typeof AnnotateFillPlateModalMachine
> | null>(null);

type AnnotateFillPlateMachineActor = ActorRefFrom<typeof AnnotateFillPlateModalMachine>;

export function useAnnotateFillPlateSelector<T = unknown>(
  selector: (snapshot: ReturnType<AnnotateFillPlateMachineActor['getSnapshot']>) => T,
  compare?: (a: T, b: T) => boolean
): T {
  // This eslint rule is unneccessary and should be removed.
  // eslint-disable-next-line benchlint/no-react-context
  const actorRef = useContext(AnnotateFillPlateMachineCtx)!;
  return useSelector(actorRef, selector, compare);
}

export const useAnnotateFillPlateActorRef = (): AnnotateFillPlateMachineActor =>
  // This eslint rule is unneccessary and should be removed.
  // eslint-disable-next-line benchlint/no-react-context
  useContext(AnnotateFillPlateMachineCtx)!;

export function AnnotateFillPlateProvider({
  children,
  ...input
}: PropsWithChildren<AnnotateFillPlateModalInput>): JSX.Element {
  const actorRef = useActorRef(AnnotateFillPlateModalMachine, {
    input,
  });

  // NotebookAnnotateFillPlateModal updates procedureContext prop async,
  // so we need to update the machine context when that happens
  useEffect(() => {
    actorRef.send({type: 'updateProcedureContext', procedureContext: input.procedureContext});
  }, [input.procedureContext, actorRef]);

  return (
    <AnnotateFillPlateMachineCtx.Provider value={actorRef}>{children}</AnnotateFillPlateMachineCtx.Provider>
  );
}
